import { FC, useState } from 'react';
import { useHistory } from 'react-router-dom';

import {
    BodyText,
    Button,
    ButtonGroup,
    Headline,
    Layout,
    Page,
    Stack,
    Togglebox,
} from '@servicetitan/design-system';

import { Role } from '../role';
import { Steps } from '../step';
import { RequestAccessHeader } from './request-access-header';
import * as Styles from './select-role-step.module.scss';

interface RoleSelectionCardProps {
    role: Role;
    title: string;
    description: string;
    selected: boolean;
    onSelect: (role: Role) => void;
}

const RoleSelectionCard: FC<RoleSelectionCardProps> = ({
    role,
    title,
    description,
    selected,
    onSelect,
}) => (
    <Togglebox
        control="radio"
        controlAlign="top"
        name="request-access-role"
        value={role}
        checked={selected}
        onClick={() => onSelect(role)}
        title={title}
        label={
            <BodyText subdued size="small">
                {description}
            </BodyText>
        }
        className={Styles.roleTogglebox}
    />
);
RoleSelectionCard.displayName = 'RoleSelectionCard';

interface SelectRoleStepProps {
    initialRole?: Role;
    onNext: (role: Role) => void;
}

export const SelectRoleStep: FC<SelectRoleStepProps> = ({ initialRole, onNext }) => {
    const history = useHistory();
    const [role, setRole] = useState<Role | undefined>(initialRole);

    return (
        <Page
            header={<RequestAccessHeader currentStep={Steps.YourRole} />}
            footer={
                <Stack className="w-100" justifyContent="flex-end">
                    <ButtonGroup>
                        <Button onClick={() => history.push('/')}>Cancel</Button>
                        <Button primary disabled={!role} onClick={() => onNext(role!)}>
                            Next
                        </Button>
                    </ButtonGroup>
                </Stack>
            }
        >
            <Stack direction="column" spacing={1}>
                <Stack direction="column" spacing={1}>
                    <Headline el="h2" size="medium">
                        Who are you building for?
                    </Headline>
                    <BodyText>
                        Select the option that best describes your situation so we can point you to
                        the right resources.
                    </BodyText>
                </Stack>

                <Layout type="2Col" spacing="compact" className="align-items-stretch-i">
                    <Layout.Section>
                        <RoleSelectionCard
                            role={Role.ThirdParty}
                            title="Third-Party Software Organization"
                            description="Distribute your app to ServiceTitan customers by completing the App Marketplace certification and application."
                            selected={role === Role.ThirdParty}
                            onSelect={setRole}
                        />
                    </Layout.Section>
                    <Layout.Section>
                        <RoleSelectionCard
                            role={Role.Customer}
                            title="ServiceTitan Customer"
                            description="Build, host, and maintain a custom app on your infrastructure exclusively for your organization and child tenants."
                            selected={role === Role.Customer}
                            onSelect={setRole}
                        />
                    </Layout.Section>
                </Layout>
            </Stack>
        </Page>
    );
};
SelectRoleStep.displayName = 'SelectRoleStep';
